import { useCallback, useEffect, useState } from 'react';
import { Card, DataTable, FormField, TextInput, Toggle, StatusBadge, ConfirmDialog, EmptyState, PageHeader, Drawer } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Icon } from '@/components/ui/Icon';
import { adminApi } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { ColorDots, LabelPill, TagActionBar, TAG_PRESETS } from './tagging/shared';
import { ActionBindingsEditor, cleanActionBindings, type ActionBinding } from './tagging/ActionBindingsEditor';

interface CallTag {
  id: number;
  slug: string;
  label: string;
  color?: string | null;
  active: boolean;
  sort_order?: number;
  actions?: ActionBinding[] | null;
}

const EMPTY = { slug: '', label: '', color: TAG_PRESETS[2], active: true, sort_order: 0, actions: [] as ActionBinding[] };

export function CallTags({ embedded = false }: { embedded?: boolean }) {
  const [rows, setRows] = useState<CallTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [drawer, setDrawer] = useState<{ mode: 'create' } | { mode: 'edit'; target: CallTag } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CallTag | null>(null);
  const [form, setForm] = useState({ ...EMPTY });

  const load = useCallback(() => {
    setLoading(true);
    void adminApi
      .callTags()
      .then((res) => { setRows(res.data as CallTag[]); setLoadError(false); })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = search
    ? rows.filter((r) => r.label.includes(search) || r.slug.toLowerCase().includes(search.toLowerCase()))
    : rows;

  // Auto-suggest a slug from the Hebrew label (kept editable).
  function onLabelChange(label: string) {
    setForm((f) => ({ ...f, label, slug: f.slug || slugify(label) }));
  }

  function openCreate() {
    setForm({ ...EMPTY });
    setDrawer({ mode: 'create' });
  }
  function openEdit(t: CallTag) {
    setForm({ slug: t.slug, label: t.label, color: t.color || TAG_PRESETS[2], active: t.active, sort_order: t.sort_order ?? 0, actions: t.actions ?? [] });
    setDrawer({ mode: 'edit', target: t });
  }

  async function save() {
    if (!form.label) { toast('יש למלא תווית', 'danger'); return; }
    try {
      const actions = cleanActionBindings(form.actions);
      if (drawer?.mode === 'edit') {
        await adminApi.updateCallTag(drawer.target.id, {
          label: form.label, color: form.color || null, active: form.active, sort_order: form.sort_order, actions,
        });
        toast('עודכן', 'ok');
      } else {
        const slug = form.slug || slugify(form.label);
        await adminApi.createCallTag({ ...form, slug, actions });
        toast('התגית נוספה', 'ok');
      }
      setDrawer(null);
      setForm({ ...EMPTY });
      load();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה';
      toast(msg, 'danger');
    }
  }

  async function remove() {
    if (!deleteTarget) return;
    try {
      await adminApi.deleteCallTag(deleteTarget.id);
      toast('התגית נמחקה', 'ok');
      setDeleteTarget(null);
      load();
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  const cols: DataTableColumn<CallTag>[] = [
    { key: 'label', he: 'תווית', render: (t) => <LabelPill color={t.color || 'var(--muted)'} label={t.label} /> },
    { key: 'slug', he: 'מזהה', render: (t) => <span dir="ltr" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{t.slug}</span> },
    { key: 'active', he: 'פעיל', render: (t) => <StatusBadge tone={t.active ? 'ok' : 'neutral'} size="sm">{t.active ? 'פעיל' : 'מושבת'}</StatusBadge> },
    {
      key: '_actions', he: 'פעולות', align: 'end',
      render: (t) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          <button type="button" className="btn ghost" style={{ height: 26, padding: '0 6px' }} onClick={() => openEdit(t)} aria-label="עריכת תגית"><Icon name="edit" size={15} /></button>
          <button type="button" className="btn ghost" style={{ height: 26, padding: '0 6px' }} onClick={() => setDeleteTarget(t)} aria-label="מחיקת תגית"><Icon name="trash" size={15} color="var(--danger)" /></button>
        </div>
      ),
    },
  ];

  return (
    <div className="stack" style={embedded ? undefined : { padding: 'var(--gap)' }}>
      {!embedded && <PageHeader title="תגיות שיחה" subtitle="תגיות שהנציג בוחר בסיכום שיחה" />}

      <TagActionBar
        search={search}
        onSearch={setSearch}
        placeholder="חיפוש תגית או מזהה…"
        createLabel="+ תגית חדשה"
        onCreate={openCreate}
      />

      {loadError && rows.length === 0 ? (
        <Card>
          <EmptyState icon="warn" title="שגיאה בטעינת התגיות"
            action={<button type="button" className="btn primary" onClick={load}>נסה שוב</button>} />
        </Card>
      ) : (
        <Card padded={false}>
          <DataTable<CallTag>
            columns={cols} rows={visible} loading={loading} onRowClick={openEdit} dense
            emptyState={
              <EmptyState icon="tag" title="אין עדיין תגיות"
                description="צור תגיות שהנציגים יוכלו לסמן בסיכום שיחה."
                action={<button type="button" className="btn primary" onClick={openCreate}>+ תגית חדשה</button>} />
            }
          />
        </Card>
      )}

      <Drawer
        isOpen={!!drawer}
        onClose={() => setDrawer(null)}
        width={400}
        title={drawer?.mode === 'edit' ? `עריכת ${drawer.target.label}` : 'תגית חדשה'}
        footer={
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn primary" onClick={() => void save()}>שמור שינויים</button>
            <button type="button" className="btn" onClick={() => setDrawer(null)}>ביטול</button>
          </div>
        }
      >
        <div className="stack" style={{ gap: 18 }}>
          <FormField label="תווית (מה שהנציג רואה)" required>
            <TextInput value={form.label} onChange={(e) => onLabelChange(e.target.value)} placeholder="לדוגמה: לקוח מעוניין" />
          </FormField>
          <FormField label="מזהה (אנגלית, ייחודי)"
            hint={drawer?.mode === 'edit' ? 'המזהה משמש למערכת ולא ניתן לשינוי לאחר יצירתו.' : undefined}>
            <TextInput value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} dir="ltr" disabled={drawer?.mode === 'edit'} />
          </FormField>
          <FormField label="צבע התווית">
            <ColorDots value={form.color} onChange={(c) => setForm({ ...form, color: c })} />
          </FormField>
          <hr style={{ border: 'none', borderTop: '1px solid var(--line)', margin: 0 }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ fontSize: 'var(--t-sm)', fontWeight: 600 }}>פעיל (זמין לנציגים)</div>
            <Toggle checked={form.active} onChange={(v) => setForm({ ...form, active: v })} />
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--line)', margin: 0 }} />
          <FormField label="הפעל בבחירת התגית">
            <ActionBindingsEditor value={form.actions} onChange={(actions) => setForm({ ...form, actions })} />
          </FormField>
        </div>
      </Drawer>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={remove}
        title="מחיקת תגית"
        message={deleteTarget ? `למחוק את התגית "${deleteTarget.label}"?` : ''}
        confirmLabel="מחק"
        tone="danger"
      />
    </div>
  );
}

function slugify(s: string): string {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || `tag-${Date.now().toString(36)}`;
}
